/**
 * Registers a genuine Quill BlockEmbed blot for the in-editor upload
 * placeholder (spinner + label shown while an image/video upload is in
 * flight).
 *
 * Inserting the placeholder via quill.clipboard.dangerouslyPasteHTML() (the
 * pattern this replaces) doesn't survive: a plain <div class="wysiwyg-upload-box">
 * has no matching Blot, so Quill's HTML->Delta conversion silently unwraps it,
 * dropping the div (and its data-upload-id attribute) and merging the label
 * text into the surrounding paragraph as plain text. The upload-completion
 * code then can never find [data-upload-id="..."] to remove it — the
 * placeholder text is left stuck in the document, and the follow-up
 * quill.setSelection(index + 1, ...) (which assumed a length-1 embed) lands
 * one character into that leftover text instead, splicing the inserted image
 * into the middle of it. See wysiwyg-product-embed-blot-utils.ts, which hit
 * and fixed the exact same class of bug for the product-embed card.
 */

import { translator } from "../translation/translator";
import { CommonUtils } from "../util/common-utils";

interface BlockEmbedInstance {
    domNode: HTMLElement;
}

interface BlockEmbedStatic {
    new (...args: never[]): BlockEmbedInstance;
    create(value?: unknown): HTMLElement;
}

interface QuillLike {
    import(name: string): unknown;
    register(formats: Record<string, unknown>, overwrite?: boolean): void;
}

interface UploadPlaceholderBlotValue {
    uploadId: string;
}

const FORMAT_NAME = "uploadPlaceholder";

let registered = false;

class WysiwygUploadPlaceholderBlotUtils {
    public static readonly formatName = FORMAT_NAME;

    public static register(Quill: QuillLike | null | undefined): void {
        if (registered || !Quill) return;

        const BlockEmbed = Quill.import("blots/block/embed") as BlockEmbedStatic | null;
        if (!BlockEmbed) return;

        class UploadPlaceholderBlot extends BlockEmbed {
            public static blotName = FORMAT_NAME;
            public static tagName = "div";
            public static className = "wysiwyg-upload-box";

            public static override create(value: UploadPlaceholderBlotValue): HTMLElement {
                const node = super.create(value) as HTMLElement;
                node.setAttribute("contenteditable", "false");
                node.setAttribute("data-upload-id", value.uploadId);

                const defaultLabel = translator?.getTranslation("wysiwyg_uploading") || "กำลังอัปโหลด...";
                const safeLabel = CommonUtils.escapeHtml(defaultLabel);

                node.innerHTML =
                    '<span class="wysiwyg-upload-box__spinner" aria-hidden="true"></span>' +
                    '<span class="wysiwyg-upload-box__label" data-translate="wysiwyg_uploading">' +
                    safeLabel +
                    "</span>";

                return node;
            }

            public static value(node: HTMLElement): UploadPlaceholderBlotValue {
                return {
                    uploadId: node.getAttribute("data-upload-id") || "",
                };
            }
        }

        Quill.register({ [`formats/${FORMAT_NAME}`]: UploadPlaceholderBlot }, true);
        registered = true;
    }
}

export { WysiwygUploadPlaceholderBlotUtils };
export type { UploadPlaceholderBlotValue };
